import { useCallback, useEffect, useMemo, useState } from 'react';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import { useIntl } from '@leagueplatform/locales';
import { useUploadFile } from './use-upload-file.hook';

/**
 * @name useUploadProfilePhoto
 * @desc React hook to upload a file, hydrate UI with file ID (existing or uploaded), handle file size error and upload error
 * @returns {array{}} [Values, Functions]
 * @returns {string} Values.profilePhotoId: the image ID stored in local state
 * @returns {object} Values.image: metadata for display in another component
 * @returns {boolean} Values.uploadError: value to show an error modal in the profile image component
 * @returns {function} Functions.setUploadError: reset an error modal in the profile image component
 * @returns {function} Functions.uploadProfilePhoto: upload the image file, set the new file ID in local state and return the new file ID
 * @returns {function} Functions.resetProfilePhotoId: reset the image to initial state (e.g. in case of profile update error)
 */

interface Values {
  profilePhotoId: string;
  image: {
    src: string;
    width: string;
    alt: string;
  };
  uploadFileIsLoading: boolean;
  uploadError?: boolean;
}

type UseUploadProfilePhotoProps = {
  avatarImageId?: string;
};

interface Functions {
  setUploadError: (value: boolean) => void;
  uploadProfilePhoto: (file: File) => Promise<string | null>;
  resetProfilePhotoId: () => void;
}

const IMAGE_FILE_SIZE_LIMIT_IN_BYTES = 1000000; // 1MB
const IMAGE_FILE_SIZE_LIMIT_IN_MEGABYTES =
  IMAGE_FILE_SIZE_LIMIT_IN_BYTES / 1000 ** 2;

export const useUploadProfilePhoto = ({
  avatarImageId,
}: UseUploadProfilePhotoProps): [Values, Functions] => {
  const { formatMessage } = useIntl();

  const profilePhotoIdInitialState = avatarImageId || '';
  const [profilePhotoId, setProfilePhotoId] = useState(
    profilePhotoIdInitialState,
  );
  const resetProfilePhotoId = () =>
    setProfilePhotoId(profilePhotoIdInitialState);

  const [uploadError, setUploadError] = useState<boolean>(false);
  const { uploadFile, uploadFileIsLoading } = useUploadFile();

  const memoizedImage = useMemo(
    () => ({
      src: profilePhotoId || '',
      width: '128px',
      alt: 'USER_PROFILE_PHOTO',
    }),
    [profilePhotoId],
  );

  useEffect(() => {
    if (avatarImageId && !profilePhotoId) {
      setProfilePhotoId(avatarImageId);
    }
  }, [avatarImageId, profilePhotoId]);

  const uploadProfilePhoto = useCallback(
    async (file: File) => {
      const fileSizeInBytes = file?.size || 0;
      if (fileSizeInBytes > IMAGE_FILE_SIZE_LIMIT_IN_BYTES) {
        toastActions.add({
          type: TOAST_STATUS.ERROR,
          textId: formatMessage(
            {
              id: 'STR_USER_AVATAR_FILE_TOO_LARGE_ERROR',
            },
            { fileSize: IMAGE_FILE_SIZE_LIMIT_IN_MEGABYTES },
          ),
        });
        return null;
      }

      const newFileId = await uploadFile(file);

      if (!newFileId) {
        setUploadError(true);
        return null;
      }

      setProfilePhotoId(newFileId);
      return newFileId;
    },
    [uploadFile, formatMessage],
  );

  return [
    {
      profilePhotoId,
      image: memoizedImage,
      uploadError,
      uploadFileIsLoading,
    },
    {
      setUploadError,
      uploadProfilePhoto,
      resetProfilePhotoId,
    },
  ];
};
